import React, { useEffect } from "react";
import { cn, useTheme, type AppTemplate, type RadiusPreset, type ThemePreset } from "@repo/core";
import { useT } from "../i18n/useT";

interface CuratedPreset {
  id: string;
  nameKey: string;
  blurbKey: string;
  template: AppTemplate;
  accent: ThemePreset;
  radius: RadiusPreset;
  swatch: [string, string, string];
}

const PRESETS: CuratedPreset[] = [
  {
    id: "clean",
    nameKey: "theme.clean",
    blurbKey: "theme.cleanBlurb",
    template: "clean",
    accent: "mint",
    radius: "lg",
    swatch: ["#2dba84", "#f0fdf8", "#e2e8f0"],
  },
  {
    id: "fintech",
    nameKey: "theme.fintech",
    blurbKey: "theme.fintechBlurb",
    template: "fintech",
    accent: "default",
    radius: "sm",
    swatch: ["#3b82f6", "#f8fafc", "#cbd5e1"],
  },
  {
    id: "minimal",
    nameKey: "theme.minimal",
    blurbKey: "theme.minimalBlurb",
    template: "minimal",
    accent: "sand",
    radius: "none",
    swatch: ["#d97706", "#fafaf9", "#e7e5e4"],
  },
  {
    id: "forest",
    nameKey: "theme.forest",
    blurbKey: "theme.forestBlurb",
    template: "forest",
    accent: "mint",
    radius: "md",
    swatch: ["#2f7e78", "#f1eee6", "#aec6c0"],
  },
];

/** Pill (`full`) removed — looks poor on dense budget UI. */
const withoutPill = (opts: { value: RadiusPreset; label: string }[]) =>
  opts.filter((r) => r.value !== "full");

export function ThemePresets() {
  const { t } = useT();
  const { theme, setTemplate, setPreset, setRadius, presets, radiusOptions } = useTheme();
  const corners = withoutPill(radiusOptions);

  // Migrate anyone still on Pill → Rounded
  useEffect(() => {
    if (theme.radius === "full") setRadius("lg");
  }, [theme.radius, setRadius]);

  const activeId =
    PRESETS.find(
      (p) =>
        p.template === theme.template &&
        p.accent === theme.preset &&
        p.radius === theme.radius
    )?.id ?? null;

  const apply = (p: CuratedPreset) => {
    setTemplate(p.template);
    setPreset(p.accent);
    setRadius(p.radius);
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold mb-2">{t("theme.curated")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => apply(p)}
              className={cn(
                "flex items-center gap-3 p-3 rounded-xl border text-left transition-colors min-h-[64px]",
                activeId === p.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/40"
              )}
            >
              <div className="flex -space-x-1 shrink-0" aria-hidden>
                {p.swatch.map((c) => (
                  <span
                    key={c}
                    className="w-6 h-6 rounded-full border border-black/10 shadow-sm"
                    style={{ background: c }}
                  />
                ))}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{t(p.nameKey)}</p>
                <p className="text-[11px] text-muted-foreground">{t(p.blurbKey)}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold mb-2">{t("theme.fineTune")}</p>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPreset(p.value)}
              className={cn(
                "flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs min-h-[40px]",
                theme.preset === p.value ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <span className="w-3.5 h-3.5 rounded-full" style={{ background: p.color }} />
              {t(`theme.accent.${p.value}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold mb-2">{t("theme.corners")}</p>
        <div className="flex flex-wrap gap-2">
          {corners.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRadius(r.value)}
              className={cn(
                "px-2.5 py-2 rounded-lg border text-xs min-h-[40px]",
                theme.radius === r.value ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              {t(`theme.radius.${r.value}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
